import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Button from "../../component/button/button"
import Header from "../../component/header/header"
import Select, { MultiValue } from "react-select"
import { FiPercent, FiBook, FiEdit2 } from "react-icons/fi"
import { RiDeleteBin6Line } from "react-icons/ri"
import Pagination from "../../component/pagination/pagination"
import { listSubject } from "src/api/subject"
import { taxonomyItemNames } from "../../utils/subjectTaxonomy"
import { deleteExamPapers, listExamPapers } from "src/api/exam"
import { ConfirmDeleteToast, LoaderComponent } from "src/utils/Helper"
import { GRADES } from "src/utils/grade"
import dayjs from "dayjs"
import toast from "react-hot-toast"
import { IoFilterOutline } from "react-icons/io5"
import { AiOutlineDownload } from "react-icons/ai"

export type ISubjects = {
  id: string
  _id: string
  name: string
  topics?: string[]
}

type SelectOption = {
  value: string
  label: string
}

const Exams = () => {
  const navigate = useNavigate()
  const [toggleFilter, setToggleFilter] = useState(false)
  const [page, setPage] = useState<number>(1)
  const [examPaper, setExamPaper] = useState<any[]>([])
  const [subjects, setSubjects] = useState<ISubjects[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const [selectedSubjects, setSelectedSubjects] = useState<
    readonly SelectOption[]
  >([])
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    readonly SelectOption[]
  >([])
  const [selectedGrades, setSelectedGrades] = useState<readonly SelectOption[]>(
    []
  )
  const [selectedStatuses, setSelectedStatuses] = useState<
    readonly SelectOption[]
  >([])

  const handleOnToggleFilter = () => {
    setToggleFilter((prev) => !prev)
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const [examRes, subjectsRes] = await Promise.all([
        listExamPapers({}),
        listSubject(1, "", 100000),
      ])

      setExamPaper(examRes.data?.data ?? [])

      const apiSubjects = subjectsRes.data?.data?.subjects ?? []

      setSubjects(
        apiSubjects.map((s: any) => ({
          id: s.id,
          name: s.title || "",
          topics: taxonomyItemNames(s.topics || []),
        }))
      )
    } catch (error) {
      console.error("Failed to fetch data:", error)
      setSubjects([])
      setExamPaper([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const applyFilters = async () => {
    setLoading(true)
    try {
      const subject = selectedSubjects[0]?.value
      const complexity = selectedDifficulty[0]?.value.toLowerCase()
      const grade = selectedGrades[0]?.value
      const status = selectedStatuses[0]?.value

      const res = await listExamPapers({
        subject,
        complexity,
        grade,
        status,
      })

      setExamPaper(res.data?.data ?? [])
    } catch (error) {
      console.error("Failed to apply filters:", error)
      setExamPaper([])
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setSelectedSubjects([])
    setSelectedDifficulty([])
    setSelectedGrades([])
    setSelectedStatuses([])
    setPage(1)

    setLoading(true)
    listExamPapers({})
      .then((res) => setExamPaper(res.data?.data ?? []))
      .catch(() => setExamPaper([]))
      .finally(() => setLoading(false))
  }

  const handleDelete = (id: string) => {
    toast((t) => (
      <ConfirmDeleteToast
        onConfirm={() => {
          setLoading(true)
          deleteExamPapers(id)
            .then(() => {
              fetchData()
            })
            .catch((er) => {
              console.log({ er })
            })
          toast.dismiss(t.id)
        }}
        onCancel={() => toast.dismiss(t.id)}
        message={`Are you sure you want to delete the exam.`}
      />
    ))
  }

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  return (
    <div className='content exams'>
      <Header
        pageTitle='Exams'
        buttonText='Create Exam'
        onClick={() => navigate("/exams/create-with-template")}
        buttonIcon={<FiPercent />}
        extraButton={
          <Button
            style={{ marginLeft: "5px" }}
            type='secondary'
            buttonType='button'
            onClick={() => navigate("/templates")}
            icon={<FiBook />}
            text='Use Templates'
          />
        }
      />

      {loading && examPaper.length === 0 ? (
        <LoaderComponent fillPage label="Loading exams…" />
      ) : (
        <div className='exam_container bg-white mt-5'>
          {/* Top Bar */}
          <div className='flex flex-row w-full gap-4 bg-white border-b border-b-gray-200 p-4 mt-5'>
            <div className='w-1/3'>
              <h3>
                Exams
                <span className='ml-2 bg-white text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-300 shadow'>
                  {examPaper.length}
                </span>
              </h3>
            </div>

            <div className='w-1/3'></div>
            <div className='w-1/3 flex gap-4 justify-end'>
              <button
                className='flex justify-center items-center gap-1.5 p-2.5 px-4 text-gray-500 font-medium text-sm leading-5 font-sans
'
                onClick={handleOnToggleFilter}
              >
                <IoFilterOutline />
                Filter
              </button>
              <button className='flex w-[125px] p-2.5 px-4 justify-center items-center gap-1.5 rounded-md border border-red-900 shadow-sm text-red-900 font-medium text-sm leading-5 font-sans'>
                <AiOutlineDownload />
                Download
              </button>
            </div>
          </div>

          {/* Filters */}
          {toggleFilter && (
            <div className='flex flex-row w-full gap-4 bg-white p-4 rounded-lg shadow-md mt-5'>
              <div className='w-3/4 flex gap-4'>
                <Select
                  name='subject_filter'
                  className='input-box'
                  options={subjects.map((subject) => ({
                    value: subject.id,
                    label: subject.name,
                  }))}
                  isMulti
                  placeholder='Select any subject'
                  value={selectedSubjects}
                  onChange={(newValue: MultiValue<SelectOption>) =>
                    setSelectedSubjects(newValue)
                  }
                />

                <Select
                  name='difficulty_level'
                  className='input-box'
                  options={[
                    { value: "Easy", label: "Easy" },
                    { value: "Medium", label: "Medium" },
                    { value: "Hard", label: "Hard" },
                  ]}
                  isMulti
                  placeholder='Select Difficulty levels'
                  value={selectedDifficulty}
                  onChange={(newValue: MultiValue<SelectOption>) =>
                    setSelectedDifficulty(newValue)
                  }
                />

                <Select
                  name='all_grades'
                  className='input-box'
                  options={GRADES}
                  isMulti
                  placeholder='Select all Grades'
                  value={selectedGrades}
                  onChange={(newValue: MultiValue<SelectOption>) =>
                    setSelectedGrades(newValue)
                  }
                />

                <Select
                  name='status_state'
                  className='input-box'
                  options={[
                    { value: "past", label: "Past" },
                    { value: "current", label: "Current" },
                    { value: "upcoming", label: "Upcoming" },
                  ]}
                  isMulti
                  placeholder='Select status'
                  value={selectedStatuses}
                  onChange={(newValue: MultiValue<SelectOption>) =>
                    setSelectedStatuses(newValue)
                  }
                />
              </div>

              <div className='w-1/4 flex flex-col gap-4'>
                <div className='w-full flex justify-end items-center space-x-2'>
                  <button
                    className='flex items-center bg-white-300 gap-2 px-4 py-1 border border-gray-400 text-brown-700 rounded hover:bg-brown-100 transition'
                    onClick={clearFilters}
                  >
                    Clear all
                  </button>
                  <button
                    className='flex items-center gap-2 px-4 py-1 bg-red-800 text-white rounded hover:bg-brown-100 transition'
                    onClick={applyFilters}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className='flex flex-row w-full border-b border-gray gap-4 bg-white p-4 mt-5'>
            <div className='flex-1 w-full overflow-x-auto mt-6 shadow-md rounded-lg'>
              <table className='w-full divide-y divide-gray-200 bg-white'>
                <thead className='bg-gray-100'>
                  <tr>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Exam Title
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Code
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Subject
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Difficulty
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Grade
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Exam Scheduled Date
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Status
                    </th>
                    <th className='px-4 py-3 text-left text-sm font-semibold text-gray-700'>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200 text-sm'>
                  {examPaper.map((exam: any, index: number) => (
                    <tr key={index} className='hover:bg-gray-50'>
                      <td className='px-4 py-[17px]'>{exam.title}</td>
                      <td className='px-4 py-[17px]'>
                        {exam.code || `EX${index + 1}`}
                      </td>
                      <td className='px-4 py-[17px]'>
                        <p className='flex items-center justify-center px-2 py-[2px] w-fit rounded-[16px] border border-[#98A2B3] bg-[#FCFCFD] text-[12px]'>
                          {
                            subjects.find(
                              (item) => item?.id === exam.subject.id
                            )?.name
                          }
                        </p>
                      </td>
                      <td className='px-4 py-[17px]'>
                        {exam.complexity || "N/A"}
                      </td>
                      <td className='px-4 py-[17px]'>{exam.grade}</td>
                      <td className='px-4 py-[17px]'>
                        {dayjs(exam.exam_scheduled_from).format("MMM DD, YYYY")}{" "}
                        - {dayjs(exam.exam_scheduled_to).format("MMM DD, YYYY")}
                      </td>

                      {/* Updated Status column */}
                      <td className='px-4 py-[17px]'>
                        {(() => {
                          const today = dayjs()
                          const startDate = dayjs(exam.exam_scheduled_from)
                          const endDate = dayjs(exam.exam_scheduled_to)

                          let statusType = ""
                          let borderColor = ""
                          let bgColor = ""
                          let textColor = ""

                          if (today.isBefore(startDate, "day")) {
                            statusType = "Upcoming"
                            borderColor = "#0BA5EC"
                            bgColor = "#F5FBFF"
                            textColor = "#1D2939"
                          } else if (today.isAfter(endDate, "day")) {
                            statusType = "Past"
                            borderColor = "#98A2B3"
                            bgColor = "#FCFCFD"
                            textColor = "#667085"
                          } else {
                            statusType = "Current"
                            borderColor = "#12B76A"
                            bgColor = "#F6FEF9"
                            textColor = "#1D2939"
                          }

                          return (
                            <span
                              className='flex items-center gap-[6px] rounded-[16px] text-xs font-normal border w-fit'
                              style={{
                                borderColor,
                                backgroundColor: bgColor,
                                color: textColor,
                                fontFamily: "Inter",
                                lineHeight: "18px",
                                padding: "2px 8px",
                              }}
                            >
                              <span
                                className='inline-block rounded-full'
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  backgroundColor: borderColor,
                                }}
                              ></span>
                              {statusType}
                            </span>
                          )
                        })()}
                      </td>

                      <td className='px-4 py-[17px] flex items-center gap-3 h-[100%]'>
                        <button
                          className='text-gray-500 hover:underline text-sm mt-[8px]'
                          onClick={() =>
                            handleNavigate(`/edit/exam/${exam?._id}`)
                          }
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className='text-gray-500 hover:underline text-sm mt-[8px]'
                          onClick={() => handleDelete(exam?._id)}
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {examPaper.length === 0 && !loading && (
                    <tr>
                      <td
                        colSpan={8}
                        className='px-4 py-6 text-center text-gray-500'
                      >
                        No exam papers found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className='flex w-full bg-white p-4 rounded-lg shadow-md mt-3'>
            <div className='ml-auto'>
              <Pagination
                postsPerPage={20}
                length={examPaper.length}
                currentPage={page}
                handlePagination={(newPage: number) => setPage(newPage)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Exams
